/**
 * TODO: MAKE THIS INTO AN IndividualsContext or something so it can fetch information about any role
 */
/* eslint-disable @typescript-eslint/camelcase */
import React, { createContext, useCallback, useContext, useState } from "react";

import { errorToast } from "src/shared/components/Toast";
import { useUpdateVolunteerShiftMutation } from "src/views/volunteer/shifts/graphql/updateVolunteerShift.generated";

import { TPrimaryRoles } from "../../constants/roles";
import { BaseShiftState, convertDayToDate } from "../constants";

interface EditShiftState extends BaseShiftState {
  updateShift: (shiftId: number) => Promise<void>;
}

const DEFAULT_STATE: EditShiftState = {
  shiftName: "",
  shiftDateStart: "",
  shiftDateEnd: "",
  timeStart: "",
  timeEnd: "",
  location: "",
  shiftLead: "",
  shiftLeadId: null,
  roles: "",
  supplementaryType: "",
  requiredVolunteers: 0,
  minVolunteers: 0,
  description: "",

  updateShiftName: () => Promise.reject(),
  updateShiftDateStart: () => Promise.reject(),
  updateShiftDateEnd: () => Promise.reject(),
  updateTimeStart: () => Promise.reject(),
  updateTimeEnd: () => Promise.reject(),
  updateLocation: () => Promise.reject(),
  updateShiftLead: () => Promise.reject(),
  updateRoles: () => Promise.reject(),
  updateSupplementaryType: () => Promise.reject(),
  updateRequiredVolunteers: () => Promise.reject(),
  updateMinVolunteers: () => Promise.reject(),
  updateDescription: () => Promise.reject(),
  updateShift: (shiftId: number) => Promise.reject(),
};

const EditShiftContext: React.Context<EditShiftState> =
  createContext(DEFAULT_STATE);

export const useEditShiftContext = () => useContext(EditShiftContext);

export const EditShiftContextProvider: React.FC = ({ children }) => {
  const [data, setData] = useState<EditShiftState>(() => DEFAULT_STATE);
  const [updateShiftMutation] = useUpdateVolunteerShiftMutation();

  const updateShiftName = useCallback((name: string) => {
    setData((prevState) => ({ ...prevState, shiftName: name }));
  }, []);

  const updateShiftDateStart = useCallback((date: string) => {
    setData((prevState) => ({ ...prevState, shiftDateStart: date }));
  }, []);

  const updateShiftDateEnd = useCallback((date: string) => {
    setData((prevState) => ({ ...prevState, shiftDateEnd: date }));
  }, []);

  const updateTimeStart = useCallback((time: string) => {
    setData((prevState) => ({ ...prevState, timeStart: time }));
  }, []);

  const updateTimeEnd = useCallback((time: string) => {
    setData((prevState) => ({ ...prevState, timeEnd: time }));
  }, []);

  const updateLocation = useCallback((location: string) => {
    setData((prevState) => ({ ...prevState, location }));
  }, []);

  const updateShiftLead = useCallback((lead: string, leadId: number | null) => {
    setData((prevState) => ({
      ...prevState,
      shiftLead: lead,
      shiftLeadId: leadId,
    }));
  }, []);

  const updateRoles = useCallback((role: string) => {
    setData((prevState) => ({ ...prevState, roles: role }));
  }, []);

  const updateSupplementaryType = useCallback((type: string) => {
    setData((prevState) => ({ ...prevState, supplementaryType: type }));
  }, []);

  const updateRequiredVolunteers = useCallback((num: number) => {
    setData((prevState) => ({ ...prevState, requiredVolunteers: num }));
  }, []);

  const updateMinVolunteers = useCallback((num: number) => {
    setData((prevState) => ({ ...prevState, minVolunteers: num }));
  }, []);

  const updateDescription = useCallback((description: string) => {
    setData((prevState) => ({ ...prevState, description }));
  }, []);

  const updateShift = useCallback(
    async (shiftId: number): Promise<void> => {
      // The id is captured when the lead is picked (or loaded from the shift
      // being edited), so it always identifies the exact account.
      if (data.shiftLeadId === null) {
        errorToast("Please select a shift lead from the dropdown.");
        throw new Error("Shift lead not selected");
      }

      try {
        const dayStart = convertDayToDate(data.shiftDateStart);
        const dayEnd = convertDayToDate(data.shiftDateEnd);

        await updateShiftMutation({
          variables: {
            data: {
              category_name:
                data.roles === TPrimaryRoles.SUPPLEMENTARY
                  ? data.supplementaryType
                  : data.roles,
              organizer_responsible_user_id: data.shiftLeadId,
              preferred_min_signups: data.minVolunteers,
              start_time: new Date(`${dayStart}T${data.timeStart}:00`),
              end_time: new Date(`${dayEnd}T${data.timeEnd}:00`),
              location: data.location,
              name: data.shiftName,
              max_count: data.requiredVolunteers,
              description: data.description,
            },
            id: shiftId,
          },
        });
      } catch (e) {
        console.error(e);
        errorToast("We couldn't save that shift. Please try again.");
        throw e;
      }
    },
    [updateShiftMutation, data]
  );

  const state = {
    ...data,
    updateShiftName,
    updateShiftDateStart,
    updateShiftDateEnd,
    updateTimeStart,
    updateTimeEnd,
    updateLocation,
    updateShiftLead,
    updateRoles,
    updateSupplementaryType,
    updateRequiredVolunteers,
    updateMinVolunteers,
    updateDescription,
    updateShift,
  };

  return (
    <EditShiftContext.Provider value={state}>
      {children}
    </EditShiftContext.Provider>
  );
};
